import { motion } from 'framer-motion';
import {
  LuBookOpen,
  LuBrain,
  LuChartLine,
  LuCheck,
  LuChevronRight,
  LuCrown,
  LuLock,
  LuRepeat,
  LuStar,
} from 'react-icons/lu';
import { Link, useNavigate } from 'react-router-dom';
import { Pill } from '../../design-system/components';
import { isLessonInProgress, isLessonUpNext } from '../../stores/next-lesson';
import { formatPercent } from '../../utils/percent';
import { type LessonMarker, lessonMarker } from './lessonMarker';
import type { ExtendedLesson } from './types';

interface LessonCardProps {
  lesson: ExtendedLesson;
  /** The last lesson in its list: the scroll fallback when everything is done. */
  isCurrent?: boolean;
  /** The chapter's next step (nextLessonId): the orange current card. */
  isNext?: boolean;
}

/** The round marker on the rail (course.css `.ee-lesson__node`). */
function MarkerIcon({ marker }: { marker: LessonMarker }) {
  switch (marker) {
    case 'locked':
      return <LuLock aria-hidden="true" />;
    case 'done':
      return <LuCheck aria-hidden="true" />;
    case 'perfect':
      return (
        <>
          <img src="/giraffe_graduated.webp" alt="Perfect score" className="ee-lesson__perfect" />
          <span className="ee-lesson__star" aria-hidden="true">
            <LuStar />
          </span>
        </>
      );
    default:
      return <LuBookOpen aria-hidden="true" />;
  }
}

/**
 * One lesson on the Course page's dotted rail (redesign PR4, 2026-10-20): a
 * round marker for its state (lessonMarker.ts), then the card. The chapter's
 * next lesson is the white card with the orange border; locked lessons are
 * muted. Same link, quiz routes, scroll anchors and words as before.
 */
export function LessonCard({ lesson, isCurrent = false, isNext = false }: LessonCardProps) {
  const navigate = useNavigate();
  const marker = lessonMarker(lesson, isNext);

  // A passed lesson's chip names its best score ("Score 85%") and opens the
  // results. It used to print "% Results" when there was no score (David,
  // 2026-10-24). With no score to name (a guest's passed lesson, or lessons
  // cached before the API sent one) it says "Results", so the link survives.
  const bestScore = lesson.passed ? lesson.best_attempt?.percentage : undefined;
  const resultsLabel =
    typeof bestScore === 'number' && Number.isFinite(bestScore)
      ? `Score ${formatPercent(bestScore)}`
      : 'Results';

  // Determine scroll priority, by the store's next-lesson rule
  // (stores/next-lesson.ts, the same rule the orange marker uses):
  // - "in-progress": has best_attempt (started but not passed) - highest priority
  // - "next": unlocked & not passed & never tried - second priority
  // - "last": last completed lesson - fallback
  const scrollPriority = isLessonInProgress(lesson)
    ? 'in-progress'
    : isLessonUpNext(lesson)
      ? 'next'
      : isCurrent
        ? 'last'
        : undefined;

  return (
    <motion.div
      data-lesson-id={lesson.id}
      data-scroll-priority={scrollPriority}
      data-marker={marker}
      className={`ee-lesson ee-lesson--${marker}`}
      variants={{
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 },
      }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      <span className="ee-lesson__node">
        <MarkerIcon marker={marker} />
      </span>
      {/* The card is not the link: the quiz and results pills are buttons,
          and a button may not sit inside an <a>. The title's link stretches
          over the whole card (course.css .ee-lesson__link::after), and the
          pill buttons sit above that layer, so the card reads and taps as
          before. */}
      <div className="ee-lesson__card">
        <div className="ee-lesson__body">
          <h3 className="ee-lesson__title">
            <Link
              to={lesson.locked ? '#' : `/lesson/${lesson.id}`}
              onClick={(e): void => {
                if (lesson.locked) e.preventDefault();
              }}
              aria-disabled={lesson.locked ? true : undefined}
              className="ee-lesson__link"
            >
              {lesson.name || lesson.title}
            </Link>
          </h3>

          <div className="ee-lesson__pills">
            {lesson.never_tried && !lesson.locked ? <Pill variant="new">NEW</Pill> : null}
            {lesson.best_attempt && !lesson.passed ? (
              <Pill variant="score">{formatPercent(lesson.best_attempt.percentage)}</Pill>
            ) : null}
            {/* Premium-only assessment badge */}
            {lesson.quiz?.premium_only && !lesson.passed ? (
              <Pill variant="premium" title="Premium Assessment — upgrade to test your skills">
                <LuCrown aria-hidden="true" />
                <span>Premium</span>
              </Pill>
            ) : null}
            {/* Quiz Button */}
            {lesson.quiz && !lesson.locked && !lesson.quiz.premium_only ? (
              <Pill
                as="button"
                variant="quiz"
                onClick={(e): void => {
                  e.preventDefault();
                  e.stopPropagation();
                  void navigate(
                    lesson.passed ? `/lesson/${lesson.id}/results` : `/quiz/${lesson.quiz?.id}`,
                  );
                }}
              >
                {lesson.passed ? (
                  <>
                    <LuChartLine aria-hidden="true" />
                    <span>{resultsLabel}</span>
                  </>
                ) : lesson.best_attempt ? (
                  <>
                    <LuRepeat aria-hidden="true" />
                    <span>Retake</span>
                  </>
                ) : (
                  <>
                    <LuBrain aria-hidden="true" />
                    <span>Quiz</span>
                  </>
                )}
              </Pill>
            ) : null}
            {/* Premium quiz: still show results if passed */}
            {lesson.quiz?.premium_only && lesson.passed && !lesson.locked ? (
              <Pill
                as="button"
                variant="quiz"
                onClick={(e): void => {
                  e.preventDefault();
                  e.stopPropagation();
                  void navigate(`/lesson/${lesson.id}/results`);
                }}
              >
                <LuChartLine aria-hidden="true" />
                <span>{resultsLabel}</span>
              </Pill>
            ) : null}
          </div>
        </div>

        {/* Chevron for navigation hint */}
        {!lesson.locked && <LuChevronRight className="ee-lesson__chev" aria-hidden="true" />}
      </div>
    </motion.div>
  );
}
